import { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemText, Collapse, Divider, CircularProgress } from '@mui/material';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { FaAnglesLeft, FaAnglesRight } from 'react-icons/fa6';
import ProjectSelection from './ProjectSelection';
import Templates from './Templates';
import UserGuide from './userGuide';
import { useSelector } from 'react-redux';
import './PreferenceSettings.scss';

const DRAWER_WIDTH = 250;
/**
 * PreferenceSettings - Main preferences page with sidebar navigation
 *  * Features:
 * - Sidebar navigation for different preference sections
 * - Dynamic content area based on selected menu item
 */
const PreferenceSettings = () => {
  const [selectedMenu, setSelectedMenu] = useState('project');
  const [selectedSubMenu, setSelectedSubMenu] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({ defaultview: true });
  const [expandedSubMenus, setExpandedSubMenus] = useState({});
  const [drawerWidth, setDrawerWidth] = useState(DRAWER_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false);
  // const [isLoading, setIsLoading] = useState(false);

const datasetsLkp = useSelector((state) => state.ctrl.datasetsLkp);  
// Reusable mapping function for transforming dataset items

const mapDatasetItems = useCallback((items) => 
  // comm_description and comm_library_element should not be the part of result
  items?.filter(item => item.lookup_code !== 'comm_description' && item.lookup_code !== 'comm_library_element')
    .map((item, index) => ({
      column_name: item.lookup_code,
      headerName: item.description,
      is_visible: true,
      column_order: index + 1
  })) || [], []);

// Define special category keys
const specialKeys = useMemo(() => ['Accs, Parts & Spares', 'Buy Resale', 'Documents & Order Services'], []);
const allInstrumentationValues = useMemo(() => 
  Object.keys(datasetsLkp).filter(key => !specialKeys.includes(key) && key !== 'Vanessa'), 
  [datasetsLkp, specialKeys]
);
console.log("datasetsLkp----------------------", datasetsLkp)
// Build modified datasets lookup object
const modifiedDatasetsLkp = useMemo(() => ({
  ...specialKeys.reduce((acc, key) => {
    acc[key] = mapDatasetItems(datasetsLkp[key]);
    return acc;
  }, {}),
  'Instrumentation': allInstrumentationValues.reduce((acc, key) => {
    acc[key] = mapDatasetItems(datasetsLkp[key]);
    return acc;
  }, {})
}), [datasetsLkp, specialKeys, allInstrumentationValues, mapDatasetItems]);

const modifiedDatasetsLkpObject = useMemo(() => ({
  ...specialKeys.reduce((acc, key) => {
    acc[key] = []
    return acc;
  }, {}),
  'Instrumentation': allInstrumentationValues.reduce((acc, key) => {
    acc[key] = []
    return acc;
  }, {})
}), [specialKeys, allInstrumentationValues]);
console.log("modifiedDatasetsLkp", modifiedDatasetsLkp)


  // Menu items for the sidebar
  const menuItems = useMemo(() => [
    { 
      id: 'defaultview', 
      label: 'Default View', 
      items: {
        'project': { label: 'Project View', items: [] },
        'package': { label: 'Package View', items: [] },
        'tagworkspace': { label: 'Workspace View', items: modifiedDatasetsLkp }
      }
    },
    { id: 'templates', label: 'Templates', items: [] },
    { 
      id: 'helpSupport', 
      label: 'Help & Support', 
      items: {       
        'Contact Support / Raise Ticket': [],
        'FAQ': [],
        'User Guide': []
        
      } 
    },
  ], [modifiedDatasetsLkp]);

  // Handle menu item click
  const handleMenuClick = (menuId) => {
    setSelectedMenu(menuId);
    setSelectedSubMenu(null);
  };

  // Handle submenu click
  const handleSubMenuClick = (menuId, subMenuId) => {
    setSelectedMenu(menuId);
    setSelectedSubMenu(subMenuId);
  };

  // Toggle expanded state for menu items
  const toggleExpanded = (menuId) => {
    setExpandedMenus(prev => {
      const isCurrentlyExpanded = prev[menuId];
      // If collapsing the menu, also collapse all its submenus
      if (isCurrentlyExpanded) {
        setExpandedSubMenus({});
      }
      return {
        ...prev,
        [menuId]: !isCurrentlyExpanded
      };
    });
  };

  // Toggle expanded state for submenu items
  const toggleSubMenuExpanded = (subMenuId) => {
    setExpandedSubMenus(prev => ({
      ...prev,
      [subMenuId]: !prev[subMenuId]
    }));
  };

  // Calculate columns for tagworkspace
  const tagWorkspaceCols = useMemo(() => {
    if (selectedMenu !== 'tagworkspace' || !selectedSubMenu) return [];
    return modifiedDatasetsLkp[selectedSubMenu] || modifiedDatasetsLkp["Instrumentation"]?.[selectedSubMenu] || [];
  }, [selectedMenu, selectedSubMenu, modifiedDatasetsLkp]);

  // Toggle drawer collapse/expand
  const toggleDrawer = () => {
    setIsDrawerCollapsed(prev => !prev);
  };

  // Handle drawer resize
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    
    const newWidth = e.clientX;
    if (newWidth >= 150 && newWidth <= 400) {
      setDrawerWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <Box className="preference-settings" sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: isDrawerCollapsed ? 40 : drawerWidth,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          '& .MuiDrawer-paper': {
            width: isDrawerCollapsed ? 40 : drawerWidth,
            boxSizing: 'border-box',
            position: 'relative',
            height: '100%',
            paddingBottom: '98px',
            borderRight:isDrawerCollapsed ? '1px solid rgba(0, 0, 0, 0)' : '1px solid rgba(0, 0, 0, 0.12)',
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
          },
        }}
      >
        {/* Collapse/Expand Toggle Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: isDrawerCollapsed ? 'center' : 'flex-end',
            padding: '4px 4px 0 4px',
            borderBottom: '1px solid rgba(0, 0, 0, 0)',
          }}
        >
          <Box
            onClick={toggleDrawer}
            sx={{
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {isDrawerCollapsed ? <FaAnglesRight style={{ fontSize: '18px', color: '#757575' }} /> : <FaAnglesLeft style={{ fontSize: '18px', color: '#757575' }} />}
          </Box>
        </Box>
        
        <List sx={{ display: isDrawerCollapsed ? 'none' : 'block' }}>
          {menuItems.map((item) => {
            const hasNestedItems = item.items && Object.keys(item.items).length > 0;
            const isExpanded = expandedMenus[item.id];

            return (
              <div key={item.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={selectedMenu === item.id && !selectedSubMenu}
                    onClick={() => {
                      if (hasNestedItems) {
                        toggleExpanded(item.id);
                      }
                      handleMenuClick(item.id);
                    }}
                  >
                    <ListItemText 
                      primary={item.label}
                      classes={{ primary: 'menu-text-primary' }}
                    />
                    {hasNestedItems && (isExpanded ? <MdExpandLess /> : <MdExpandMore />)}
                  </ListItemButton>
                </ListItem>
                    
                {/* Level 2: Nested items (for defaultview: project, package, tagworkspace) */}
                {hasNestedItems && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {Object.keys(item.items).map((subKey) => {
                        const subItem = item.items[subKey];
                        // Check if this is an object with label property (like project/package/tagworkspace under defaultview)
                        const isDefaultViewChild = item.id === 'defaultview' && subItem.label;
                        const displayLabel = isDefaultViewChild ? subItem.label : subKey;
                        const subItemContent = isDefaultViewChild ? subItem.items : subItem;
                        
                        // Check if this submenu has further nested items
                        const hasNestedSubItems = typeof subItemContent === 'object' && !Array.isArray(subItemContent) && Object.keys(subItemContent).length > 0;
                        const isSubExpanded = expandedSubMenus[subKey];

                        return (
                          <div key={subKey}>
                            <ListItem disablePadding>
                              <ListItemButton
                                sx={{ pl: 4, fontSize: '15px' }}
                                selected={isDefaultViewChild
                                  ? selectedMenu === subKey && !selectedSubMenu
                                  : selectedMenu === item.id && selectedSubMenu === subKey}
                                onClick={() => {
                                  if (hasNestedSubItems) {
                                    toggleSubMenuExpanded(subKey);
                                  }
                                  if (isDefaultViewChild) {
                                    handleMenuClick(subKey);
                                  } else {
                                    handleSubMenuClick(item.id, subKey);
                                  }
                                }}
                              >
                                <ListItemText 
                                  primary={displayLabel} 
                                  classes={{ primary: 'menu-text-level1' }}
                                />
                                {hasNestedSubItems && <span style={{ fontSize: '15px' }}>{isSubExpanded ? <MdExpandLess /> : <MdExpandMore />}</span>}
                              </ListItemButton>
                            </ListItem>

                            {/* Level 3: Nested sub-items (for Workspace View: Instrumentation, Accs, etc) */}
                            {hasNestedSubItems && (
                              <Collapse in={isSubExpanded} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                  {Object.keys(subItemContent).reverse().map((nestedKey) => {
                                    const nestedItem = subItemContent[nestedKey];
                                    // Check if this has even deeper nesting (level 4: like DP Flow under Instrumentation)
                                    const hasDeepNestedItems = typeof nestedItem === 'object' && !Array.isArray(nestedItem) && Object.keys(nestedItem).length > 0;
                                    const isDeepExpanded = expandedSubMenus[`${subKey}-${nestedKey}`];

                                    return (
                                      <div key={nestedKey}>
                                        <ListItem disablePadding>
                                          <ListItemButton
                                            sx={{ pl: 8 }}
                                            selected={selectedMenu === subKey && selectedSubMenu === nestedKey}
                                            onClick={() => {
                                              if (hasDeepNestedItems) {
                                                toggleSubMenuExpanded(`${subKey}-${nestedKey}`);
                                              }
                                              handleSubMenuClick(subKey, nestedKey);
                                            }}
                                          >
                                            <ListItemText 
                                              primary={nestedKey}
                                              classes={{ primary: 'menu-text-level2' }}
                                            />
                                            {hasDeepNestedItems && <span style={{ fontSize: '15px' }}>{isDeepExpanded ? <MdExpandLess /> : <MdExpandMore />}</span>}
                                          </ListItemButton>
                                        </ListItem>

                                        {/* Level 4: Deep nested items (DP Flow, Flow, Pressure, etc. under Instrumentation) */}
                                        {hasDeepNestedItems && (
                                          <Collapse in={isDeepExpanded} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding>
                                              {Object.keys(nestedItem).map((deepNestedKey) => (
                                                <ListItem key={deepNestedKey} disablePadding>
                                                  <ListItemButton
                                                    sx={{ pl: 12 }}
                                                    selected={selectedMenu === subKey && selectedSubMenu === deepNestedKey}
                                                    onClick={() => handleSubMenuClick(subKey, deepNestedKey)}
                                                  >
                                                    <ListItemText 
                                                      primary={deepNestedKey}
                                                      classes={{ primary: 'menu-text-level3' }}
                                                    />
                                                  </ListItemButton>
                                                </ListItem>
                                              ))}
                                            </List>
                                            <Divider sx={{ my: 1, mx: 2, borderColor: 'rgba(0, 0, 0, 0.3)' }} />
                                          </Collapse>
                                        )}
                                      </div>
                                    );
                                  })}
                                </List>
                                {!isDefaultViewChild && <Divider sx={{ my: 1, mx: 2, borderColor: 'rgba(0, 0, 0, 0.3)' }} />}
                              </Collapse>
                            )}
                          </div>
                        );
                      })}
                    </List>
                    {item.id !== 'defaultview' && <Divider sx={{ my: 1, mx: 2, borderColor: 'rgba(0, 0, 0, 0.3)' }} />}
                  </Collapse>
                )}
              </div>
            );
          })}
        </List>
        
        {/* Resize handle */}
        {!isDrawerCollapsed && (
          <Box
            onMouseDown={handleMouseDown}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '5px',
              cursor: 'col-resize',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              },
              transition: 'background-color 0.2s',
              zIndex: 1,
            }}
          />
        )}
      </Drawer>

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <div className="preference-content full-width">
          {selectedMenu === 'templates' ? (
            <Templates />
          ) : selectedMenu === 'helpSupport' && selectedSubMenu === 'User Guide' ? (
            <UserGuide />
          ) : selectedMenu === 'helpSupport' ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                minHeight: '400px',
              }}
            >
              <h2 style={{ color: '#666', fontSize: '24px', fontWeight: '500' }}>
                Upcoming Features
              </h2>
            </Box>
          ) : (
            <ProjectSelection 
              type={selectedMenu} 
              cols={tagWorkspaceCols}
              selectedSubMenu={selectedSubMenu}
              loopupData={modifiedDatasetsLkpObject}
            />
          )}
        </div>
      </Box>
    </Box>
  );
};

export default PreferenceSettings;
